import React, { useCallback, useEffect, useRef, useState } from "react";
import Header from "./../Layout/Header";
import FooterPremium from "./../Layout/FooterPremium";
import PageTitle from "./../Layout/PageTitle";
import "./AjmanExhibition.css";

const imageContext = require.context(
	"../../images/gallery/ajman-exhibition",
	false,
	/\.jpg$/
);

const PHOTO_META = [
	{ file: "exhibition-01.jpg", w: 1264, h: 843 },
	{ file: "wa-1715-3.jpg", w: 1200, h: 1600 },
	{ file: "arw-04110.jpg", w: 2000, h: 1333 },
	{ file: "wa-1716.jpg", w: 1200, h: 1600 },
	{ file: "vsp-04156.jpg", w: 2000, h: 1333 },
	{ file: "wa-1718-1.jpg", w: 899, h: 1599 },
	{ file: "arw-04113.jpg", w: 2000, h: 1333 },
	{ file: "wa-1717.jpg", w: 1200, h: 1600 },
	{ file: "wa-0054.jpg", w: 2000, h: 1500 },
	{ file: "wa-1718.jpg", w: 960, h: 1280 },
	{ file: "arw-04120.jpg", w: 2000, h: 1333 },
	{ file: "wa-3634.jpg", w: 900, h: 1600 },
	{ file: "vsp-04186.jpg", w: 2000, h: 1333 },
	{ file: "wa-1719.jpg", w: 1200, h: 1600 },
	{ file: "arw-04126.jpg", w: 2000, h: 1333 },
	{ file: "wa-1717-1.jpg", w: 900, h: 1600 },
	{ file: "wa-1715.jpg", w: 1280, h: 960 },
	{ file: "wa-1718-2.jpg", w: 899, h: 1599 },
	{ file: "arw-04130.jpg", w: 2000, h: 1333 },
	{ file: "wa-5748.jpg", w: 720, h: 1280 },
	{ file: "vsp-04203.jpg", w: 2000, h: 1333 },
	{ file: "wa-1718-3.jpg", w: 899, h: 1599 },
	{ file: "wa-1719-1.jpg", w: 1600, h: 1204 },
	{ file: "arw-04234.jpg", w: 2000, h: 1333 },
	{ file: "wa-1715-1.jpg", w: 1280, h: 960 },
	{ file: "wa-2531.jpg", w: 1280, h: 960 },
	{ file: "arw-04250.jpg", w: 2000, h: 1333 },
	{ file: "wa-1715-2.jpg", w: 1280, h: 960 },
	{ file: "wa-1719-2.jpg", w: 1600, h: 1204 },
	{ file: "wa-0054-1.jpg", w: 1600, h: 1200 },
];

function imageUrl(file) {
	const loaded = imageContext(`./${file}`);
	return loaded && loaded.default ? loaded.default : loaded;
}

const PHOTOS = PHOTO_META.map((photo, index) => ({
	...photo,
	src: imageUrl(photo.file),
	alt: `Ajman Real Estate Investment Exhibition 2026, frame ${index + 1}`,
}));

function AjmanExhibition() {
	const touchX = useRef(0);
	const swiped = useRef(false);
	const [active, setActive] = useState(null);

	useEffect(() => {
		document.title = "Ajman Real Estate Investment Exhibition 2026 | Star Giga";
	}, []);

	useEffect(() => {
		if (active === null) return undefined;
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		const onKey = (event) => {
			if (event.key === "Escape") setActive(null);
			if (event.key === "ArrowRight") setActive((index) => (index + 1) % PHOTOS.length);
			if (event.key === "ArrowLeft") setActive((index) => (index - 1 + PHOTOS.length) % PHOTOS.length);
		};
		window.addEventListener("keydown", onKey);
		return () => {
			document.body.style.overflow = previous;
			window.removeEventListener("keydown", onKey);
		};
	}, [active]);

	const current = active === null ? null : PHOTOS[active];

	const step = useCallback((direction) => {
		setActive((index) => (index + direction + PHOTOS.length) % PHOTOS.length);
	}, []);

	return (
		<div className="ajman-page page-content bg-white">
			<Header />
			<PageTitle motherMenu="Gallery" activeMenu="Gallery" />

			<section className="ajman-gallery">
				<div className="section-head">
					<span className="ajman-heading-eyebrow">Gallery</span>
					<h2 className="title ajman-heading">Ajman Real Estate, Investment Exhibition 2026</h2>
					<div className="ajman-heading-rule" aria-hidden="true" />
				</div>
				<div className="ajman-grid">
					{PHOTOS.map((photo, index) => (
						<button
							key={photo.file}
							type="button"
							className="ajman-tile"
							onClick={() => setActive(index)}
							aria-label={`Open ${photo.alt}`}
						>
							<img src={photo.src} alt={photo.alt} loading="lazy" />
							<span className="ajman-tile-icon" aria-hidden="true">
								<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
									<path d="M5 1H1v4M9 1h4v4M5 13H1V9M9 13h4V9" stroke="currentColor" strokeWidth="1.4" />
								</svg>
							</span>
						</button>
					))}
				</div>
			</section>

			{current && (
				<div
					className="ajman-lightbox"
					role="dialog"
					aria-modal="true"
					aria-label="Photograph preview"
					onClick={() => {
						if (swiped.current) {
							swiped.current = false;
							return;
						}
						setActive(null);
					}}
					onTouchStart={(event) => {
						touchX.current = event.changedTouches[0].clientX;
					}}
					onTouchEnd={(event) => {
						const delta = event.changedTouches[0].clientX - touchX.current;
						if (Math.abs(delta) > 48) {
							swiped.current = true;
							step(delta > 0 ? -1 : 1);
						}
					}}
				>
					<button type="button" className="ajman-lightbox-close" aria-label="Close" onClick={() => setActive(null)}>
						×
					</button>
					<button
						type="button"
						className="ajman-lightbox-nav prev"
						aria-label="Previous photograph"
						onClick={(event) => {
							event.stopPropagation();
							step(-1);
						}}
					>
						‹
					</button>
					<figure className="ajman-lightbox-figure" onClick={(event) => event.stopPropagation()}>
						<img src={current.src} alt={current.alt} />
						<figcaption className="ajman-lightbox-caption">
							{active + 1} / {PHOTOS.length} · Ajman Real Estate Investment Exhibition 2026
						</figcaption>
					</figure>
					<button
						type="button"
						className="ajman-lightbox-nav next"
						aria-label="Next photograph"
						onClick={(event) => {
							event.stopPropagation();
							step(1);
						}}
					>
						›
					</button>
				</div>
			)}
			<FooterPremium />
		</div>
	);
}

export default AjmanExhibition;
